import React from 'react';

import GlobalStyles from '@mui/material/GlobalStyles';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { onboardColors } from '../theme/onboardTokens';

import type { ReactNode } from 'react';

// Single source of truth for the onboard font family. Source Sans Pro is the brand
// font; the rest of the stack is fallback only.
export const ONBOARD_FONT_FAMILY =
  "'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// The host's ThemeContext (app/javascript/components/ThemeContext/ThemeContext.tsx)
// boosts modal/snackbar/tooltip z-index by +10000 over MUI's defaults so overlays
// always win against other stacking contexts. The host theme isn't importable here
// (built from a `useTheme()` base at render time, not a static export), so the boosted
// values are mirrored directly. MUI's un-boosted defaults are modal: 1300,
// snackbar: 1400, tooltip: 1500 - see
// https://mui.com/material-ui/customization/z-index/. Keep in sync with ThemeContext.tsx.
const DEFAULT_ZINDEX = createTheme().zIndex;
const ZINDEX_BOOST = 10000;

// Minimal theme: sets the base font for every MUI component so components no longer
// declare `fontFamily` inline (intentional exceptions — monospace token displays,
// Georgia document previews — keep their own explicit `fontFamily`).
//
// Also mirrors the host's boosted zIndex tokens (see above) - without this, Onboard's
// Autocomplete/Dialog/Tooltip poppers render at MUI's un-boosted defaults and can
// appear behind siblings raised by the host's boost (e.g. the approver dropdown in
// Offers/CreateOffer/steps/ApprovalStep). Exposes the color tokens on the theme as
// `theme.onboard.colors` (typed via module augmentation below).
const onboardTheme = createTheme({
  typography: { fontFamily: ONBOARD_FONT_FAMILY },
  zIndex: {
    modal: DEFAULT_ZINDEX.modal + ZINDEX_BOOST,
    snackbar: DEFAULT_ZINDEX.snackbar + ZINDEX_BOOST,
    tooltip: DEFAULT_ZINDEX.tooltip + ZINDEX_BOOST
  },
  onboard: { colors: onboardColors }
});

// Module augmentation: lets `theme.onboard.colors.brand.primary` type-check.
declare module '@mui/material/styles' {
  interface Theme {
    onboard: { colors: typeof onboardColors };
  }

  interface ThemeOptions {
    onboard?: { colors: typeof onboardColors };
  }
}

// Form controls do not inherit font-family by default, so set them explicitly.
const globalFontStyles = {
  body: { fontFamily: ONBOARD_FONT_FAMILY },
  'input, button, textarea, select': { fontFamily: 'inherit' }
};

interface Props {
  children: ReactNode;
}

const OnboardThemeProvider = ({ children }: Props) => (
  <ThemeProvider theme={onboardTheme}>
    <GlobalStyles styles={globalFontStyles} />
    {children}
  </ThemeProvider>
);

export default OnboardThemeProvider;
